import { ReactElement } from "react";

import {
  CodeSecurityFindingStatus,
  CodeSecurityIssueSeverity,
  FindingPriority,
  FindingState,
} from "@oneleet/api_client";
import { colors } from "@oneleet/config/colors";

import {
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  ChevronsUp,
  Info,
} from "lucide-react";

import { Badge } from "@oneleet/components";

type BadgeColors = Parameters<typeof Badge>["0"]["color"];

export const legacySortOrderBySeverity = {
  [FindingPriority.CRITICAL]: 4,
  [FindingPriority.HIGH]: 3,
  [FindingPriority.MEDIUM]: 2,
  [FindingPriority.LOW]: 1,
  [FindingPriority.INFORMATIONAL]: 0,
};

export const sortOrderBySeverity = {
  [CodeSecurityIssueSeverity.CRITICAL]: 4,
  [CodeSecurityIssueSeverity.HIGH]: 3,
  [CodeSecurityIssueSeverity.MEDIUM]: 2,
  [CodeSecurityIssueSeverity.LOW]: 1,
  [CodeSecurityIssueSeverity.INFO]: 0,
};

export const legacyFindingPriorityToTextColor: {
  [key in FindingPriority]: string;
} = {
  [FindingPriority.CRITICAL]: colors.red[700],
  [FindingPriority.HIGH]: colors.red[500],
  [FindingPriority.MEDIUM]: colors.yellow[500],
  [FindingPriority.LOW]: colors.green[500],
  [FindingPriority.INFORMATIONAL]: colors.blue[500],
};

export const findingSeverityToTextColor: {
  [key in CodeSecurityIssueSeverity]: string;
} = {
  [CodeSecurityIssueSeverity.CRITICAL]: "text-red-700",
  [CodeSecurityIssueSeverity.HIGH]: "text-red-500",
  [CodeSecurityIssueSeverity.MEDIUM]: "text-yellow-500",
  [CodeSecurityIssueSeverity.LOW]: "text-green-500",
  [CodeSecurityIssueSeverity.INFO]: "text-blue-500",
};

export const legacyFindingPriorityToIcon: {
  [key in FindingPriority]: ReactElement<{ className?: string }>;
} = {
  [FindingPriority.CRITICAL]: <AlertTriangle />,
  [FindingPriority.HIGH]: <ChevronsUp />,
  [FindingPriority.MEDIUM]: <ChevronUp />,
  [FindingPriority.LOW]: <ChevronDown />,
  [FindingPriority.INFORMATIONAL]: <Info />,
};

export const findingSeverityToIcon: {
  [key in CodeSecurityIssueSeverity]: ReactElement<{ className?: string }>;
} = {
  [CodeSecurityIssueSeverity.CRITICAL]: <AlertTriangle />,
  [CodeSecurityIssueSeverity.HIGH]: <ChevronsUp />,
  [CodeSecurityIssueSeverity.MEDIUM]: <ChevronUp />,
  [CodeSecurityIssueSeverity.LOW]: <ChevronDown />,
  [CodeSecurityIssueSeverity.INFO]: <Info />,
};

export const legacyFindingPriorityToBadgeColor: {
  [key in FindingPriority]: BadgeColors;
} = {
  [FindingPriority.CRITICAL]: "darkRed",
  [FindingPriority.HIGH]: "red",
  [FindingPriority.MEDIUM]: "yellow",
  [FindingPriority.LOW]: "green",
  [FindingPriority.INFORMATIONAL]: "blue",
};

export const findingSeverityToBadgeColor: {
  [key in CodeSecurityIssueSeverity]: BadgeColors;
} = {
  [CodeSecurityIssueSeverity.CRITICAL]: "darkRed",
  [CodeSecurityIssueSeverity.HIGH]: "red",
  [CodeSecurityIssueSeverity.MEDIUM]: "yellow",
  [CodeSecurityIssueSeverity.LOW]: "green",
  [CodeSecurityIssueSeverity.INFO]: "blue",
};

export const legacyFindingStateToBadgeColor: {
  [key in FindingState]?: BadgeColors;
} = {
  [FindingState.OPEN]: "red",
  [FindingState.REMEDIATED]: "green",
  [FindingState.FALSE_POSITIVE]: "blue",
  [FindingState.ACCEPTED_RISK]: "blue",
  [FindingState.DUPLICATE]: "gray",
};

export const findingStateToBadgeColor: {
  [key in CodeSecurityFindingStatus]?: BadgeColors;
} = {
  [CodeSecurityFindingStatus.OPEN]: "red",
  [CodeSecurityFindingStatus.IN_PROGRESS]: "yellow",
  [CodeSecurityFindingStatus.RESOLVED]: "green",
  [CodeSecurityFindingStatus.SUPPRESSED]: "blue",
};

export const legacyFindingPriorityToReadableText: {
  [key in FindingPriority]: string;
} = {
  [FindingPriority.CRITICAL]: "Critical",
  [FindingPriority.HIGH]: "High",
  [FindingPriority.MEDIUM]: "Medium",
  [FindingPriority.LOW]: "Low",
  [FindingPriority.INFORMATIONAL]: "Info",
};

export const findingStateToReadableText: {
  [key in CodeSecurityFindingStatus]: string;
} = {
  [CodeSecurityFindingStatus.OPEN]: "Open",
  [CodeSecurityFindingStatus.IN_PROGRESS]: "In Progress",
  [CodeSecurityFindingStatus.RESOLVED]: "Resolved",
  [CodeSecurityFindingStatus.SUPPRESSED]: "Suppressed",
};

export const legacyFindingPriorityToReadableSentenceText: {
  [key in FindingPriority]: string;
} = {
  [FindingPriority.CRITICAL]: "critical",
  [FindingPriority.HIGH]: "high severity",
  [FindingPriority.MEDIUM]: "medium severity",
  [FindingPriority.LOW]: "low severity",
  [FindingPriority.INFORMATIONAL]: "informational",
};

export const findingSeverityToReadableSentenceText: {
  [key in CodeSecurityIssueSeverity]: string;
} = {
  [CodeSecurityIssueSeverity.CRITICAL]: "critical",
  [CodeSecurityIssueSeverity.HIGH]: "high severity",
  [CodeSecurityIssueSeverity.MEDIUM]: "medium severity",
  [CodeSecurityIssueSeverity.LOW]: "low severity",
  [CodeSecurityIssueSeverity.INFO]: "informational",
};
